import React, { useContext, useEffect, useMemo, useState } from "react";

import { Tooltip, Menu } from "@mui/material";

import EyeOff from "./EyeOff";
import Table from "./Table";
import Context from "../../context-index";
import { Column } from "../../export";
import useStyles from "./styles";

type Props = {
  columns: Column[];
};

const ColumnChooser = ({ columns }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | SVGElement>(null);

  const { selectedColumns, setColumns } = useContext(Context);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const classes = useStyles();

  const blockedColumns = useMemo(
    () =>
      columns.reduce((filtered, column, index) => {
        if (column?.hideShowColumn || column?.pinned) {
          filtered.push(index);
        }

        return filtered;
      }, [] as number[]),
    [columns]
  );

  // old code logic
  // const allColumnsHidden = useMemo(() => {
  //   if (!!blockedColumns.length) {
  //     return ![...selectedColumns.filter((value) => !blockedColumns.includes(value))].length;
  //   } else {
  //     return !selectedColumns.filter((_, index) => index !== 0).length;
  //   }
  // }, [selectedColumns]);

  // Old logic check if column array = 1 -> set allColumnsHidden = true because this ui block cannot remove all columns
  const allColumnsHidden = useMemo(() => {
    const validSelectedColumns = selectedColumns.filter((col) => col >= 0); // ✅ Remove invalid (-1) values

    if (blockedColumns.length > 0) {
      return validSelectedColumns.every((value) => blockedColumns.includes(value));
    } else {
      return validSelectedColumns.length === 1 && validSelectedColumns[0] === 0;
    }
  }, [selectedColumns, blockedColumns]);

  const handleHideAll = () => {
    if (!!blockedColumns?.length) {
      setColumns([...selectedColumns.filter((value) => blockedColumns.includes(value))]);
    } else {
      setColumns([selectedColumns[0]]);
    }
  };

  const handleShowAll = () => {
    setColumns(columns.map((_, index) => index));
  };

  return (
    <>
      <Tooltip data-cy="column-chooser" title={"Show Column Chooser"} placement="bottom">
        <div className={classes.flex} onClick={handleClick}>
          <Table className={classes.icon} isOpen={Boolean(anchorEl)} />
        </div>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            padding: "4px 0px 4px 0px",
            borderRadius: "12px",
            minWidth: "224px",
          },
        }}
        MenuListProps={{
          style: {
            padding: 0,
          },
        }}
      >
        <div data-cy="container-menu-column-chooser">
          <div className={classes.list}>
            <div className={classes.header}>
              <span>Shown</span>
              <span
                className={classes.hideAll}
                onClick={allColumnsHidden ? handleShowAll : handleHideAll}
              >
                {allColumnsHidden ? "Show all" : "Hide all"}
              </span>
            </div>

            {columns.map(({ name, title, hideShowColumn }, index) => {
              let checked = !!selectedColumns.find((value) => value === index);

              // Prevent action if it's a blocked column or the last remaining column
              const isDisabled =
                hideShowColumn || (selectedColumns.length === 1 && selectedColumns[0] === index);

              return (
                <div
                  style={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.5 : 1, // Reduce visibility for non-clickable columns
                    pointerEvents: isDisabled ? "none" : "auto", // Prevent clicking
                  }}
                  data-cy={name && name.toLowerCase()}
                  key={index}
                  className={classes.columnOption}
                  onClick={() => {
                    if (isDisabled) return; // Prevent clicks on locked columns

                    if (checked) {
                      if (selectedColumns.length <= 1) return; // Prevent removing the last column
                      setColumns([...selectedColumns.filter((value) => value !== index)]);
                    } else {
                      setColumns([...selectedColumns, index]);
                    }
                  }}
                >
                  <span style={{ color: checked ? "#1D2939" : "#D0D5DD" }}>{title}</span>
                  <EyeOff disabled={!checked} />
                </div>
              );
            })}
          </div>
        </div>
      </Menu>
    </>
  );
};

export default ColumnChooser;
